import { useEffect } from "react";
import { ReactNode, useState } from "react";
import { createContext } from "react";
import { WorkedDays } from "../../../../../libs/worked-days";
import { PointsByDayRange } from "../../../model";
import { dateFromInitial, dateToInitial } from "../../../../../libs/dateUtils";
import { calcTotalHours, calcTotalHoursInSeconds } from "../../../lib";
import useAuthData from "../../../../../hook/useAppData";
import { getPointsByDayRange } from "../../../service";

interface StoreProviderProps {
  children: ReactNode;
}

interface StoreContextProps {
  needSpinner: boolean;
  setNeedSpinner: any;
  dateGridList: PointsByDayRange[];
  dateGridListOriginal: PointsByDayRange[];
  dateFrom: string;
  setDateFrom: any;
  dateTo: string;
  setDateTo: any;
  setBankHours: any;
  setTotalHours: any;
  totalHours: any;
  bankHours: any;
  getPointsRange: any;
  page: number;
  setPage;
  selectSize: number;
  setSelectSize;
  renderHeaderTable;
}

interface ITimePoint {
  date: string;
  projectsTime: [{ projectName: string; workedProjectTime: string }];
  workedTime: string;
}

export const TimeSheetStore = createContext({} as StoreContextProps);
export function TimesheetStoreProvider({ children }: StoreProviderProps) {
  const { token, userId } = useAuthData();
  const [data, setData] = useState([] as PointsByDayRange[]);
  const [needSpinner, setNeedSpinner] = useState(true);
  const [dateFrom, setDateFrom] = useState(dateFromInitial);
  const [dateTo, setDateTo] = useState(dateToInitial);
  const [dateGridList, setDateGridList] = useState(data);
  const [dateGridListOriginal, setDateGridListOriginal] = useState(data);
  const [bankHours, setBankHours] = useState(0);
  const [totalHours, setTotalHours] = useState("00:00:00");

  const [page, setPage] = useState(1);
  const [selectSize, setSelectSize] = useState(10);

  useEffect(() => {
    getPointsRange(dateFromInitial, dateToInitial);
  }, []);

  function getPointsRange(dateFrom: string, dateTo: string) {
    getPointsByDayRange(token, userId, dateFrom, dateTo).then((dataReq: PointsByDayRange[]) => {
      setData(dataReq);
      if (!data && data === dataReq) setNeedSpinner(false);
    });
  }

  useEffect(() => {
    if (data?.length) {
      setDateGridList(data);
      if (totalHours === "00:00:00") getBankHours(data);
    } else {
      setDateGridList(data);

      setNeedSpinner(true);
      setTimeout(() => {
        setNeedSpinner(false);
      }, 500);
    }
  }, [data]);

  useEffect(() => {
    if (dateGridList?.length) {
      setTimeout(() => {
        setNeedSpinner(false);
      }, 300);
    }
  }, [dateGridList]);

  function getBankHours(data: any) {
    setTotalHours(calcTotalHours(data));
    const bankHours = calcBankHours(expectHoursInSeconds(), calcTotalHoursInSeconds(data));
    setBankHours(bankHours);
  }

  function renderHeaderTable() {
    const numberOfProjects = dateGridList?.map((timePoint: ITimePoint) => {
      return timePoint?.projectsTime?.length;
    });
    return numberOfProjects?.reduce((item, acc) => {
      if (item > acc) return item;
      return acc;
    }, 0);
  }

  return (
    <TimeSheetStore.Provider
      value={{
        needSpinner,
        setNeedSpinner,
        dateGridList,
        dateGridListOriginal,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        setBankHours,
        setTotalHours,
        bankHours,
        totalHours,
        getPointsRange,
        page,
        setPage,
        selectSize,
        setSelectSize,
        renderHeaderTable,
      }}
    >
      {children}
    </TimeSheetStore.Provider>
  );
}

function expectHoursInSeconds(firstDay?: any, day?: number, month?: number): number {
  return new WorkedDays().calculateWorkingDaysInMonth(firstDay, day, month) * 8 * 3600;
}

function calcBankHours(expectHours: number, realityHours: number): number {
  return Math.floor((realityHours - expectHours) / 3600);
}
